// Shared, app-wide constants. Mirrors the `constants.ts` convention from
// stratum-analytical (adapted to plain JS / CRA env vars).

export const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';

// Colours live in styles/variables.css, not here. A location's colour is used
// as a CSS value everywhere it appears, so the token is the value — nothing has
// to stay in step with a hex written in two places.
export const LOCATION_META = {
  toronto:    { name: 'Shangri-La',     color: 'var(--loc-toronto)',    city: 'Toronto' },
  foresthill: { name: 'Forest Hill',    color: 'var(--loc-foresthill)', city: 'Toronto' },
  vancouver:  { name: 'Hyatt Alberni',  color: 'var(--loc-vancouver)',  city: 'Vancouver' },
  waterfront: { name: 'VIP Waterfront', color: 'var(--loc-waterfront)', city: 'Vancouver' },
};

// The cities the garages sit in, in the order the filter shows them.
export const CITIES = [...new Set(Object.values(LOCATION_META).map(l => l.city))];

// A location's colour at reduced strength. `${color}66` cannot work once the
// colour is a token rather than a hex string, so the mix is done in CSS.
export const tint = (color, percent) =>
  `color-mix(in srgb, ${color} ${percent}%, transparent)`;

export const STATUS_CYCLE   = { available: 'busy', busy: 'break', break: 'available' };
export const STATUS_LABEL   = { available: 'AVAILABLE', busy: 'BUSY', break: 'BREAK' };
export const PRIORITY_CYCLE  = { low: 'medium', medium: 'high', high: 'urgent', urgent: 'low' };
export const PRIORITY_LABEL  = { low: 'LOW', medium: 'MEDIUM', high: 'HIGH', urgent: 'URGENT' };

// TODO: Cash and Square Terminal need the amount field
// TODO: if amount higher than service amount on the card, that will be tip after amount + tax.
// For example in toronto service is $100, the amount is $120, then $113, $7 is tip
export const NEXT_STEP_META = {
  started:  { title: 'Start Appointment',  prompt: 'Do you want to start this appointment?', requiredPhotos: 3, phase: 'before', confirmLabel: 'Start' },
  finished: { title: 'Finish Appointment', prompt: 'Is this appointment finished?',          requiredPhotos: 3, phase: 'after',  confirmLabel: 'Finish' },
  upsells:  { title: 'Upsells',            prompt: 'Are there any upsells?',                  requiredPhotos: 0, phase: 'photo',  confirmLabel: 'Confirm' },
  payment:  { title: 'Payment',            prompt: 'Has payment been collected?',             requiredPhotos: 0, phase: 'photo',  confirmLabel: 'Confirm' },
  posted:   { title: 'Posted',             prompt: 'Mark this appointment as posted?',        requiredPhotos: 0, phase: 'photo',  confirmLabel: 'Confirm' },
};

export const ROLE_LABELS = {
  staff: 'Staff',
  admin: 'Admin',
  share_device: 'Shared Device',
};

// Roles restricted to the shared-device view — a wall display in the bay, which
// sees only what is safe to leave on screen in public. Named for the role
// rather than for what it can currently reach, since the permitted surface will
// grow. Kept here so both the router and the pages can read it without
// importing each other.
export const SHARE_DEVICE_ONLY_ROLES = ['share_device'];
export const isShareDeviceOnly = (user) => SHARE_DEVICE_ONLY_ROLES.includes(user?.role);

export const PAGES_BY_ROLE = {
  share_device: ['queue'],
  admin: [
    'queue', 'appointments', 'payments', 'attention',
    'tips', 'receivables', 'analytics',
    // An admin reads the shifts page rather than working from it: they pick a
    // technician and see that person's week, with every request button off.
    'shifts',
    'users', 'staff', 'schedule', 'timesheets',
  ],
  staff: [
    'queue', 'shifts', 'appointments', 'payments', 'attention', 'staff',
  ],
};

// An unrecognised or missing role is treated as staff — the ordinary user.
// Falling back to nothing would lock out any account whose role was never set,
// and falling back to admin would hand it the money reports.
const pagesFor = (user) => PAGES_BY_ROLE[user?.role] || PAGES_BY_ROLE.staff;

export const canViewPage = (user, page) => pagesFor(user).includes(page);

// Which page key a path belongs to, so the router can ask the same question the
// nav does. Longest match first: '/reports/tips' must beat '/reports'.
export const ROUTE_PAGES = [
  ['/dashboard',            'queue'],
  ['/shifts',               'shifts'],
  ['/appointments',         'appointments'],
  ['/transaction',          'payments'],
  ['/attention',            'attention'],
  ['/reports/tips',         'tips'],
  ['/reports/receivables',  'receivables'],
  ['/reports/analytics',    'analytics'],
  ['/admin',                'users'],
  ['/staff',                'staff'],
  ['/schedule',             'schedule'],
  ['/timesheets',           'timesheets'],
];

export const HOME_PATH = '/dashboard';
